<template>
	<div v-if="isOpen" class="modal-overlay" @click="onOverlayClick">
		<form class="modal-content" @click.stop @submit.prevent="onSave">
			<div class="modal-header">
				<h3>{{ $t("adminPanelPage.createUserModal.title") }}</h3>
				<button type="button" @click="closeModal" class="close-btn" :disabled="saving">&times;</button>
			</div>
			<div class="modal-body">
				<div v-if="submitError" class="modal-error">{{ submitError }}</div>

				<label class="field">
					<span class="field-label">{{ $t("adminPanelPage.createUserModal.firstName") }}</span>
					<input
						v-model="firstName"
						type="text"
						class="field-input"
						:placeholder="$t('adminPanelPage.createUserModal.firstNamePlaceholder')"
						:disabled="saving"
						autocomplete="given-name"
					/>
				</label>

				<label class="field">
					<span class="field-label">{{ $t("adminPanelPage.createUserModal.lastName") }}</span>
					<input
						v-model="lastName"
						type="text"
						class="field-input"
						:placeholder="$t('adminPanelPage.createUserModal.lastNamePlaceholder')"
						:disabled="saving"
						autocomplete="family-name"
					/>
				</label>

				<label class="field">
					<span class="field-label">{{ $t("adminPanelPage.createUserModal.email") }}</span>
					<input
						v-model="email"
						type="email"
						class="field-input"
						:placeholder="$t('adminPanelPage.createUserModal.emailPlaceholder')"
						:disabled="saving"
						autocomplete="email"
					/>
				</label>

				<div class="field">
					<span class="field-label">{{ $t("adminPanelPage.createUserModal.role") }}</span>
					<ArkListboxDropdown
						:model-value="roleId"
						@update:model-value="handleRoleChange"
						:options="roleOptions"
						:multiple="false"
						:disabled="saving"
						:clearable="false"
						:searchable="false"
						:placeholder="$t('adminPanelPage.createUserModal.rolePlaceholder')"
						:no-options-text="$t('adminPanelPage.createUserModal.noRoles')"
						:same-width="true"
					/>
				</div>
			</div>
			<div class="modal-footer">
				<Button variant="secondary" size="m" type="button" :disabled="saving" @click="closeModal">
					{{ $t("adminPanelPage.createUserModal.cancel") }}
				</Button>
				<Button variant="primary" size="m" type="submit" :disabled="saving || !canSave">
					{{ saving ? $t("adminPanelPage.createUserModal.saving") : $t("adminPanelPage.createUserModal.save") }}
				</Button>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import Button from "@/components/ui/Button.vue"

type RoleOption = {
	value: number
	label: string
}

interface Props {
	isOpen: boolean
	roleOptions: RoleOption[]
	saving?: boolean
	submitError?: string
}

interface Emits {
	(e: "close"): void
	(e: "save", payload: { firstName: string; lastName: string; email: string; roleId: number }): void
}

const props = withDefaults(defineProps<Props>(), {
	saving: false,
	submitError: "",
})

const emit = defineEmits<Emits>()

const firstName = ref("")
const lastName = ref("")
const email = ref("")
const roleId = ref<number | null>(null)

const canSave = computed(
	() =>
		firstName.value.trim() !== "" && lastName.value.trim() !== "" && email.value.trim() !== "" && roleId.value != null
)

watch(
	() => props.isOpen,
	open => {
		if (open) {
			firstName.value = ""
			lastName.value = ""
			email.value = ""
			roleId.value = null
		}
	}
)

const handleRoleChange = (value: string | number | null | (string | number | null)[]) => {
	if (Array.isArray(value)) {
		roleId.value = value[0] == null ? null : Number(value[0])
		return
	}

	roleId.value = value == null ? null : Number(value)
}

const closeModal = () => {
	if (!props.saving) {
		emit("close")
	}
}

const onOverlayClick = () => {
	closeModal()
}

const onSave = () => {
	if (!canSave.value || roleId.value == null) return

	emit("save", {
		firstName: firstName.value.trim(),
		lastName: lastName.value.trim(),
		email: email.value.trim(),
		roleId: roleId.value,
	})
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.modal-content {
	background: white;
	border-radius: 8px;
	width: 90%;
	max-width: 520px;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;

	h3 {
		margin: 0;
		color: #333;
	}

	.close-btn {
		background: none;
		border: none;
		font-size: 24px;
		cursor: pointer;
		color: #666;
		padding: 0;
		width: 30px;
		height: 30px;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover:not(:disabled) {
			color: #333;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}
}

.modal-body {
	padding: 20px;

	.modal-error {
		color: #dc3545;
		margin-bottom: 16px;
		font-size: 0.95rem;
	}

	.field {
		display: block;
		margin-bottom: 16px;
	}

	.field-label {
		display: block;
		margin-bottom: 6px;
		font-weight: 500;
		color: #333;
		font-size: 0.9rem;
	}

	.field-input {
		width: 100%;
		box-sizing: border-box;
		padding: 8px 12px;
		border: 1px solid #ced4da;
		border-radius: 4px;
		font-size: 1rem;
		font-family: inherit;
		min-height: 40px;
	}

	.field-input:disabled {
		background: #f1f3f5;
		cursor: not-allowed;
	}
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 16px 20px;
	border-top: 1px solid #e0e0e0;
	background-color: #f8f9fa;
}
</style>
